import { tenantApi } from '@shared/api/tenantApi';
import { unwrapList } from '@shared/api/unwrap';

/**
 * Documents — `GET /public/documents/folders` and `GET /public/documents`.
 * Download streams via `GET /public/documents/:id/download` (auth required).
 */

export interface DocumentRecord {
  document_id: string;
  title: string;
  category?: string;
  description?: string;
  file_name: string;
  file_size_bytes?: number;
  mime_type?: string;
  uploaded_at: string;
  folder_id?: string | null;
}

export interface DocumentCategory {
  category_id: string;
  name: string;
  count: number;
}

interface RawDocumentRow {
  document_id: string;
  title?: string;
  description?: string | null;
  file_name?: string;
  file_size?: number | null;
  mime_type?: string | null;
  created_at?: string;
  uploaded_at?: string;
  folder_id?: string | null;
  document_type?: { name?: string } | string;
}

interface DocumentFolder {
  folder_id: string;
  name: string;
  document_count?: number;
}

function documentTypeName(raw: RawDocumentRow): string | undefined {
  const dt = raw.document_type;
  if (!dt) return undefined;
  if (typeof dt === 'string') return dt;
  return dt.name;
}

function toIsoDate(value: unknown): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

export function mapDocumentRow(
  raw: RawDocumentRow,
  folderNames?: Map<string, string>,
): DocumentRecord | null {
  const uploaded_at = toIsoDate(raw.uploaded_at ?? raw.created_at);
  if (!uploaded_at) return null;

  const title = (raw.title ?? raw.file_name ?? 'Document').trim();
  const file_name = (raw.file_name ?? title).trim() || 'document';

  const category =
    (raw.folder_id && folderNames?.get(raw.folder_id)) ||
    documentTypeName(raw) ||
    undefined;

  return {
    document_id: raw.document_id,
    title,
    category,
    description: raw.description ?? undefined,
    file_name,
    file_size_bytes: raw.file_size ?? undefined,
    mime_type: raw.mime_type ?? undefined,
    uploaded_at,
    folder_id: raw.folder_id ?? undefined,
  };
}

export async function fetchDocumentCategories(): Promise<DocumentCategory[]> {
  const { data } = await tenantApi.get('/public/documents/folders');
  const folders = unwrapList<DocumentFolder>(data);
  return folders.map((f) => ({
    category_id: f.folder_id,
    name: f.name,
    count: f.document_count ?? 0,
  }));
}

export async function fetchDocuments(
  folderId?: string,
  folderNames?: Map<string, string>,
): Promise<DocumentRecord[]> {
  const params: Record<string, string | number> = { page: 1, limit: 100 };
  if (folderId) {
    params.folder_id = folderId;
  }

  const { data } = await tenantApi.get('/public/documents', { params });
  const rows = unwrapList<RawDocumentRow>(data);
  return rows
    .map((row) => mapDocumentRow(row, folderNames))
    .filter((doc): doc is DocumentRecord => doc !== null);
}

/** Stream document bytes through the tenant API (same as member portal). */
export async function downloadDocumentBlob(documentId: string): Promise<Blob> {
  const { data } = await tenantApi.get(`/public/documents/${documentId}/download`, {
    responseType: 'blob',
  });
  return data as Blob;
}
